/**
 * The geometry of the Team photo tool, with no browser in it: where to cut a group photo into
 * tiles for the face finder, how to join the faces the tiles found, and which square of the photo
 * becomes a profile photo. Every number is in pixels of the photo the owner picked.
 */

export type Box = { x: number; y: number; w: number; h: number };
export type ScoredBox = Box & { score: number };

/** A square part of the photo: the profile photo before it is scaled to 512 x 512. */
export type Square = { x: number; y: number; side: number };

/**
 * The tile sizes, as a part of the long side of the photo. The face finder (BlazeFace full range)
 * looks at a small square picture, so a face must fill a fair part of what it sees. The whole photo
 * finds the big faces; the smaller tiles find the faces at the back of a group.
 */
export const TILE_FRACTIONS = [1, 0.5, 0.25, 0.15] as const;
/** Tiles overlap by a quarter, so a face on a tile edge is whole in the next tile. */
const TILE_OVERLAP = 0.25;

/** The tiles to look at, for a photo of this size. */
export function tilePlan(width: number, height: number, fractions: readonly number[] = TILE_FRACTIONS): Box[] {
  const tiles: Box[] = [];
  const seen = new Set<string>();
  const add = (tile: Box) => {
    const key = `${tile.x},${tile.y},${tile.w},${tile.h}`;
    if (seen.has(key)) return;
    seen.add(key);
    tiles.push(tile);
  };
  const long = Math.max(width, height);
  for (const fraction of fractions) {
    if (fraction >= 1) {
      add({ x: 0, y: 0, w: width, h: height });
      continue;
    }
    const side = Math.round(long * fraction);
    const w = Math.min(side, width);
    const h = Math.min(side, height);
    const step = Math.max(1, Math.round(side * (1 - TILE_OVERLAP)));
    for (let y = 0; ; y += step) {
      const top = Math.min(y, height - h);
      for (let x = 0; ; x += step) {
        add({ x: Math.min(x, width - w), y: top, w, h });
        if (x + w >= width) break;
      }
      if (y + h >= height) break;
    }
  }
  return tiles;
}

function area(box: Box): number {
  return Math.max(0, box.w) * Math.max(0, box.h);
}

function overlap(a: Box, b: Box): number {
  const w = Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x);
  const h = Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y);
  return w > 0 && h > 0 ? w * h : 0;
}

/** Intersection over union: 1 for the same box, 0 for boxes that do not touch. */
export function iou(a: Box, b: Box): number {
  const inter = overlap(a, b);
  return inter === 0 ? 0 : inter / (area(a) + area(b) - inter);
}

/**
 * The same face, found in more than one tile, becomes one face: the best scored box wins. A box
 * that sits mostly inside a better one (a part of a face seen in a small tile) is dropped too.
 */
export function mergeFaces(boxes: readonly ScoredBox[], limit = 0.3): ScoredBox[] {
  const sorted = [...boxes].sort((a, b) => b.score - a.score);
  const kept: ScoredBox[] = [];
  for (const box of sorted) {
    const same = kept.some((other) => iou(other, box) > limit || overlap(other, box) > 0.6 * Math.min(area(other), area(box)));
    if (!same) kept.push(box);
  }
  // Reading order: top to bottom in rows, then left to right, so the numbers on screen make sense.
  return kept.sort((a, b) => {
    const row = Math.min(a.h, b.h) * 0.6;
    return Math.abs(a.y - b.y) > row ? a.y - b.y : a.x - b.x;
  });
}

/** A face this small in the photo makes a blurred profile photo. */
export const SMALL_FACE_SIDE = 64;

/**
 * The square around a face that becomes the profile photo: the head with some space around it and
 * a little of the shoulders. Inside the photo, and smaller when the photo is smaller.
 */
export function squareForFace(face: Box, width: number, height: number): Square {
  const faceSide = Math.max(face.w, face.h);
  const side = Math.min(Math.round(faceSide * 2.2), width, height);
  const centreX = face.x + face.w / 2;
  // A little below the middle of the face, so the chin is not at the bottom edge.
  const centreY = face.y + face.h * 0.58;
  return clampSquare({ x: centreX - side / 2, y: centreY - side / 2, side }, width, height);
}

/** Keeps a square inside the photo: never larger than the photo, moved back inside when it is out. */
export function clampSquare(square: Square, width: number, height: number): Square {
  const side = Math.max(1, Math.min(square.side, width, height));
  const x = Math.min(Math.max(0, square.x), width - side);
  const y = Math.min(Math.max(0, square.y), height - side);
  return { x: Math.round(x), y: Math.round(y), side: Math.round(side) };
}

/**
 * Zoom a square around its centre. `factor` above 1 shows more of the photo. The square never gets
 * smaller than `minSide` or larger than the photo.
 */
export function zoomSquare(square: Square, factor: number, width: number, height: number, minSide: number): Square {
  const side = Math.min(Math.max(square.side * factor, minSide), width, height);
  const centreX = square.x + square.side / 2;
  const centreY = square.y + square.side / 2;
  return clampSquare({ x: centreX - side / 2, y: centreY - side / 2, side }, width, height);
}

/** Move a square by some photo pixels, kept inside the photo. */
export function moveSquare(square: Square, dx: number, dy: number, width: number, height: number): Square {
  return clampSquare({ ...square, x: square.x + dx, y: square.y + dy }, width, height);
}

/** A box the owner drew by hand, from two corners in any order. Null when it is too small to be a face. */
export function boxFromCorners(a: { x: number; y: number }, b: { x: number; y: number }, minSide = 12): Box | null {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  const w = Math.abs(a.x - b.x);
  const h = Math.abs(a.y - b.y);
  if (w < minSide || h < minSide) return null;
  return { x: Math.round(x), y: Math.round(y), w: Math.round(w), h: Math.round(h) };
}

/** The board never gets taller than this part of its width, nor flatter than the other. */
const VIEW_MAX_TALL = 0.85;
const VIEW_MIN_TALL = 0.6;

/** Grows a view to an aspect the board can show, inside the photo. */
export function fitView(view: Box, width: number, height: number): Box {
  let { x, y, w, h } = view;
  if (h / w > VIEW_MAX_TALL) {
    const grown = h / VIEW_MAX_TALL;
    x -= (grown - w) / 2;
    w = grown;
  } else if (h / w < VIEW_MIN_TALL) {
    const grown = w * VIEW_MIN_TALL;
    y -= (grown - h) / 2;
    h = grown;
  }
  w = Math.min(w, width);
  h = Math.min(h, height);
  x = Math.min(Math.max(0, x), width - w);
  y = Math.min(Math.max(0, y), height - h);
  return { x, y, w, h };
}

/**
 * The part of the photo with the faces in it, with some room around them: in a group photo the
 * people are often a strip under a lot of sky. The whole photo when there are no faces.
 */
export function viewOfFaces(faces: readonly Box[], width: number, height: number): Box {
  if (faces.length === 0) return { x: 0, y: 0, w: width, h: height };
  const sides = faces.map((face) => Math.max(face.w, face.h)).sort((a, b) => a - b);
  const median = sides[Math.floor(sides.length / 2)]!;
  const pad = Math.max(median * 1.5, Math.max(width, height) * 0.03);
  const left = Math.max(0, Math.min(...faces.map((face) => face.x)) - pad);
  const top = Math.max(0, Math.min(...faces.map((face) => face.y)) - pad);
  const right = Math.min(width, Math.max(...faces.map((face) => face.x + face.w)) + pad);
  const bottom = Math.min(height, Math.max(...faces.map((face) => face.y + face.h)) + pad * 1.5);
  return fitView({ x: left, y: top, w: right - left, h: bottom - top }, width, height);
}

/**
 * Zoom the view around a point. `factor` below 1 zooms in. Never smaller than `minWidth`, never
 * larger than the photo, and the aspect stays the same.
 */
export function zoomView(view: Box, factor: number, centre: { x: number; y: number }, width: number, height: number, minWidth: number): Box {
  const aspect = view.h / view.w;
  const maxWidth = Math.min(width, height / aspect);
  const w = Math.min(Math.max(view.w * factor, Math.min(minWidth, maxWidth)), maxWidth);
  const h = w * aspect;
  const x = Math.min(Math.max(0, centre.x - w / 2), width - w);
  const y = Math.min(Math.max(0, centre.y - h / 2), height - h);
  return { x, y, w, h };
}

/** Move the view by some photo pixels, kept inside the photo. */
export function panView(view: Box, dx: number, dy: number, width: number, height: number): Box {
  return {
    ...view,
    x: Math.min(Math.max(0, view.x + dx), width - view.w),
    y: Math.min(Math.max(0, view.y + dy), height - view.h),
  };
}

/** True when the box is fully inside the view. */
export function inView(box: Box, view: Box): boolean {
  return box.x >= view.x && box.y >= view.y && box.x + box.w <= view.x + view.w && box.y + box.h <= view.y + view.h;
}
